import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import {
  CROP_KEY_PREFIX,
  FARM_KEY_PREFIX,
  FARMS_URL_PARAM_DIAGNOSTICS_LENS,
  FARMS_URL_PARAM_GROUP_BY,
  FARMS_URL_PARAM_LENS,
  FARMS_URL_PARAM_NODE,
  FARMS_URL_PARAM_PLANNING_LENS,
  FARMS_URL_PARAM_TAB,
  FARMS_URL_PARAM_YEAR,
  INPUT_KEY_PREFIX,
  ORG_KEY_PREFIX,
  PADDOCK_KEY_PREFIX,
} from 'constants/routeConstants';
import {
  INPUT_DOC_TAB_DIAGNOSTICS,
  INPUT_DOC_TAB_INSIGHTS,
  INPUT_DOC_TAB_MAP,
  INPUT_DOC_TAB_PLANNING,
  TREE_GROUP_BY_CROP,
  TREE_GROUP_BY_FARM,
} from 'constants/uiConstants';
import { useVisibleOrgData } from 'hooks/useVisibleOrgData';
import { ZoneLinkType } from 'interfaces/IInput';
import { RootState } from 'redux/Store';
import {
  filterInputsByYear,
  getInputsForPaddock,
  parseTreeYearFilter,
} from 'utils/FarmUtils';

import {
  setActiveTreeNodeKey,
  setSelectedFarmId,
  setSelectedPaddockId,
  setTreeGroupBy,
  setTreeYearFilter,
} from '../../../redux/slices/FarmSlice';
import { setSelectedInputId } from '../../../redux/slices/InputSlice';
import {
  setFarmDiagnosticsLens,
  setInsightsActiveLens,
  setInsightsActiveTab,
  setPlanningLens,
} from '../../../redux/slices/InsightsSlice';
import { deriveScopeLevel } from 'pages/FarmsPage/Workspace/Insights/insightUtils';

import { resolveDiagnosticsLensParam } from 'pages/FarmsPage/hooks/resolveDiagnosticsLens';
import { resolveInsightsLensParam } from './resolveInsightsLensParam';
import {
  resolveMovedInsightsLens,
  resolvePlanningLens,
} from './resolvePlanningLens';

// Single source of truth syncing the /farms URL into Redux (OP-2260).
const useFarmsNavSync = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const { organisationId: activeOrganisationId } = useSelector(
    (state: RootState) => state.activeOrganisationReducer
  );
  const activeTreeNodeKey = useSelector(
    (state: RootState) => state.farmReducer.activeTreeNodeKey
  );
  const treeGroupBy = useSelector(
    (state: RootState) => state.farmReducer.treeGroupBy
  );
  const treeYearFilter = useSelector(
    (state: RootState) => state.farmReducer.treeYearFilter
  );
  const activeTab = useSelector(
    (state: RootState) => state.insightsReducer.activeTab
  );
  const activeLens = useSelector(
    (state: RootState) => state.insightsReducer.activeLens
  );
  const diagnosticsLens = useSelector(
    (state: RootState) => state.insightsReducer.farmDiagnosticsLens
  );
  const planningLens = useSelector(
    (state: RootState) => state.insightsReducer.planningLens
  );

  // Excluded farms/paddocks and their plans resolve like deleted ones — a
  // node pointing at one falls back to the org root (OP-2390).
  const {
    farms,
    inputs,
    farmsQuery: { isPending: isFarmsPending },
    inputsQuery: { isPending: isInputsPending },
  } = useVisibleOrgData(activeOrganisationId);

  // tab / lens / groupBy / year — plain value params, no tree-data dependency.
  useEffect(() => {
    const paramTab = searchParams.get(FARMS_URL_PARAM_TAB);

    // Nutrition and Rotation moved from Insights to Planning (OP-2549): an
    // old link or bookmark to either reopens it there. The rewritten URL runs
    // this effect again.
    const movedLens = resolveMovedInsightsLens(
      paramTab,
      searchParams.get(FARMS_URL_PARAM_LENS),
      INPUT_DOC_TAB_INSIGHTS
    );
    if (movedLens) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete(FARMS_URL_PARAM_TAB);
          next.delete(FARMS_URL_PARAM_LENS);
          next.set(FARMS_URL_PARAM_PLANNING_LENS, movedLens);
          return next;
        },
        { replace: true }
      );
      return;
    }

    const nextTab =
      paramTab === INPUT_DOC_TAB_INSIGHTS
        ? INPUT_DOC_TAB_INSIGHTS
        : paramTab === INPUT_DOC_TAB_DIAGNOSTICS
          ? INPUT_DOC_TAB_DIAGNOSTICS
          : paramTab === INPUT_DOC_TAB_MAP
            ? INPUT_DOC_TAB_MAP
            : INPUT_DOC_TAB_PLANNING;
    if (nextTab !== activeTab) dispatch(setInsightsActiveTab(nextTab));

    const nextLens = resolveInsightsLensParam(
      searchParams.get(FARMS_URL_PARAM_LENS)
    );
    if (nextLens !== activeLens) dispatch(setInsightsActiveLens(nextLens));

    const scopeLevel = deriveScopeLevel(activeTreeNodeKey, {
      input: INPUT_KEY_PREFIX,
      paddock: PADDOCK_KEY_PREFIX,
      farm: FARM_KEY_PREFIX,
      crop: CROP_KEY_PREFIX,
      org: ORG_KEY_PREFIX,
    });

    // No `dlens` param means the default for the current scope: a farm or org
    // opens Rotation efficiency, a paddock or plan opens WUE trend (OP-2390).
    const nextDiagnosticsLens = resolveDiagnosticsLensParam(
      searchParams.get(FARMS_URL_PARAM_DIAGNOSTICS_LENS),
      scopeLevel
    );
    if (nextDiagnosticsLens !== diagnosticsLens)
      dispatch(setFarmDiagnosticsLens(nextDiagnosticsLens));

    // No `plens` param, or one the scope doesn't offer, means the scope's
    // default: Paddock plan for a plan or paddock, Rotation for a farm or the
    // organisation (OP-2549).
    const nextPlanningLens = resolvePlanningLens(
      searchParams.get(FARMS_URL_PARAM_PLANNING_LENS),
      scopeLevel
    );
    if (nextPlanningLens !== planningLens)
      dispatch(setPlanningLens(nextPlanningLens));

    const paramGroupBy = searchParams.get(FARMS_URL_PARAM_GROUP_BY);
    const nextGroupBy =
      paramGroupBy === TREE_GROUP_BY_CROP
        ? TREE_GROUP_BY_CROP
        : TREE_GROUP_BY_FARM;
    if (nextGroupBy !== treeGroupBy) dispatch(setTreeGroupBy(nextGroupBy));

    // No year param means "current calendar year" (matches Redux initial state).
    // TREE_YEAR_ALL ('all') is written explicitly when the user selects all years.
    const nextYearFilter = parseTreeYearFilter(
      searchParams.get(FARMS_URL_PARAM_YEAR)
    );
    if (nextYearFilter !== treeYearFilter)
      dispatch(setTreeYearFilter(nextYearFilter));
  }, [
    searchParams,
    activeTab,
    activeLens,
    diagnosticsLens,
    planningLens,
    activeTreeNodeKey,
    treeGroupBy,
    treeYearFilter,
    dispatch,
    setSearchParams,
  ]);

  // node — waits for tree data so a real deletion isn't mistaken for not-loaded-yet.
  useEffect(() => {
    if (!activeOrganisationId) return;
    if (isFarmsPending || isInputsPending) return;

    const orgKey = `${ORG_KEY_PREFIX}${activeOrganisationId}`;
    const node = searchParams.get(FARMS_URL_PARAM_NODE);

    const applySelection = (
      key: string,
      farmId: string | null,
      paddockId: string | null,
      inputId: string | null
    ) => {
      if (activeTreeNodeKey !== key) dispatch(setActiveTreeNodeKey(key));
      dispatch(setSelectedFarmId(farmId));
      dispatch(setSelectedPaddockId(paddockId));
      dispatch(setSelectedInputId(inputId));
    };

    const resetToOrgRoot = () => {
      applySelection(orgKey, null, null, null);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete(FARMS_URL_PARAM_NODE);
          return next;
        },
        { replace: true }
      );
    };

    if (!node) {
      applySelection(orgKey, null, null, null);
      return;
    }

    if (node.startsWith(INPUT_KEY_PREFIX)) {
      const inputId = node.replace(INPUT_KEY_PREFIX, '');
      const input = inputs.find((i) => i.id === inputId);
      if (!input) {
        resetToOrgRoot();
        return;
      }

      const links = input.zone?.links || [];
      const paddockLink = links.find(
        (link) => link.linkType === ZoneLinkType.Paddock
      );
      const farmLink = links.find(
        (link) => link.linkType === ZoneLinkType.Farm
      );
      const paddockId = paddockLink?.linkId ?? null;
      // The paddock lookup can miss when the paddock was deleted on
      // another device, in which case the tree renders the plan under its farm
      // (groupInputsByFarmHierarchy) — so fall back to the farm link rather
      // than leaving the selection with no farm at all.
      const farmId =
        (paddockId
          ? (farms.find((farm) =>
              farm.paddocks.some((p) => p.paddockId === paddockId)
            )?.id ?? null)
          : null) ??
        farmLink?.linkId ??
        null;

      applySelection(node, farmId, paddockId, inputId);
      return;
    }

    if (node.startsWith(PADDOCK_KEY_PREFIX)) {
      const paddockId = node.replace(PADDOCK_KEY_PREFIX, '');
      const farm = farms.find((f) =>
        f.paddocks.some((p) => p.paddockId === paddockId)
      );
      if (!farm) {
        resetToOrgRoot();
        return;
      }

      // Selecting a paddock opens straight into its plan for the active year
      // only when there's exactly one — anything else (none, or several) is
      // ambiguous, so the Planning tab is left to show its own "no plan" /
      // "choose a plan" state instead of this silently guessing one.
      const paddockPlans = filterInputsByYear(
        getInputsForPaddock(inputs, paddockId),
        treeYearFilter
      );
      const solePlanId = paddockPlans.length === 1 ? paddockPlans[0].id : null;

      applySelection(node, farm.id, paddockId, solePlanId);
      return;
    }

    if (node.startsWith(FARM_KEY_PREFIX)) {
      const farmId = node.replace(FARM_KEY_PREFIX, '');
      const farm = farms.find((f) => f.id === farmId);
      if (!farm) {
        resetToOrgRoot();
        return;
      }

      applySelection(node, farmId, null, null);
      return;
    }

    if (node.startsWith(CROP_KEY_PREFIX) || node.startsWith(ORG_KEY_PREFIX)) {
      applySelection(node, null, null, null);
      return;
    }

    // Unrecognised node value — fall back to the org root.
    resetToOrgRoot();
  }, [
    searchParams,
    setSearchParams,
    activeOrganisationId,
    activeTreeNodeKey,
    farms,
    inputs,
    treeYearFilter,
    isFarmsPending,
    isInputsPending,
    dispatch,
  ]);
};

export default useFarmsNavSync;
